import { createAsyncThunk } from "@reduxjs/toolkit"

import {
  downloadFileFromResponse,
  getApiErrorMessage,
  handleApiError
} from "../../../Utils/thunkHelpers"
import * as services from "../services"
import { ALERTS_SLICE_NAME } from "./alerts.constants"

/* ------------------------------------------------------------------ *
 * Alerts list, statistics and filter options
 * ------------------------------------------------------------------ */

export const getAlertsStatistics = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/get_alerts_statistics`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAlertsStatistics(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertsFilterOptions = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/get_alerts_filter_options`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAlertsFilterOptions(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlerts = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/get_alerts`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAlerts(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertsExport = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/get_alerts_export`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.getAlertsExport(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

/* ------------------------------------------------------------------ *
 * Single alert: update, pin, details, logs
 * ------------------------------------------------------------------ */

export const updateAlert = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/update_alert`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.updateAlert(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data || params
  }
)

export const updateAlertFromDetailsPage = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/update_alert_from_details_page`,
  async (params, { dispatch, rejectWithValue }) => {
    const alertId = params.alert_id
    const response = await services.updateAlert(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(await getApiErrorMessage(response))
    }
    dispatch(getAlertDetails(alertId))
    dispatch(getAlertLogs(alertId))
    return response.data
  }
)

export const updateAlertTransaction = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/update_alert_transaction`,
  async ({ refreshDetails, ...requestParams }, { dispatch, rejectWithValue }) => {
    const alertId = requestParams.alert_id
    const response = await services.updateAlert(requestParams)
    if (!response.ok) {
      const errorMessage = (await getApiErrorMessage(response)) || "Server response error"
      return rejectWithValue(errorMessage)
    }
    if (refreshDetails && alertId) {
      dispatch(getAlertDetails(alertId))
      dispatch(getAlertLogs(alertId))
    }
    return response.data
  }
)

export const pinAlert = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/pin_alert`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.pinAlert(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertDetails = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/get_alert_details`,
  async (alertID, { dispatch, rejectWithValue }) => {
    const response = await services.getAlertDetails(alertID)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertLogs = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/get_alert_logs`,
  async (alertID, { dispatch, rejectWithValue }) => {
    const response = await services.getAlertLogs(alertID)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const getAlertDetailsExport = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/get_alert_details_export`,
  async (alertID, { dispatch, rejectWithValue }) => {
    const response = await services.getAlertDetailsExport(alertID)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    downloadFileFromResponse(response, "Report.csv")
  }
)

/* ------------------------------------------------------------------ *
 * Support ticket / credit request / bulk resolve
 * ------------------------------------------------------------------ */

export const createAlertTicket = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/create_alert_ticket`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.createAlertTicket(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data || params
  }
)

export const createCreditRequest = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/create_credit_request`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.createCreditRequest(params)
    if (!response.ok) {
      await handleApiError(dispatch, response)
      return rejectWithValue(response.data)
    }
    return response.data
  }
)

export const alertsBulkResolve = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/alerts_bulk_resolve`,
  async (data, { rejectWithValue }) => {
    const response = await services.alertsBulkResolve(data)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)

export const bulkUpdateAlertStatus = createAsyncThunk(
  `${ALERTS_SLICE_NAME}/bulk_update_alert_status`,
  async (data, { rejectWithValue }) => {
    const response = await services.bulkUpdateAlertStatus(data)
    if (!response.ok) return rejectWithValue(response.data)
    return response.data
  }
)
